import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Check, Sparkles, X } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

/**
 * Design system §11 — toasts sit bottom-right and auto-dismiss after 5s.
 * Dark panel for confirmations, white with a danger border for problems.
 */
export type ToastTone = 'success' | 'ai' | 'error';

export interface ToastAction {
  readonly label: string;
  onClick: () => void;
}

export interface ToastInput {
  readonly message: string;
  readonly tone?: ToastTone;
  readonly action?: ToastAction;
  /** Milliseconds on screen. `null` keeps it until dismissed. */
  readonly durationMs?: number | null;
}

interface ToastRecord extends ToastInput {
  readonly id: number;
}

interface ToastContextValue {
  toast: (input: ToastInput) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((input: ToastInput) => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((current) => [...current, { ...input, id }]);
  }, []);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-100 flex flex-col items-end gap-2 sm:left-auto sm:right-6 sm:bottom-6"
      >
        {toasts.map((item) => (
          <ToastItem key={item.id} toast={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: number) => void }) {
  const tone = toast.tone ?? 'success';
  const duration = toast.durationMs === undefined ? DEFAULT_DURATION_MS : toast.durationMs;

  useEffect(() => {
    if (duration === null) return;
    const timer = window.setTimeout(() => {
      onDismiss(toast.id);
    }, duration);
    return () => {
      window.clearTimeout(timer);
    };
  }, [duration, onDismiss, toast.id]);

  const error = tone === 'error';

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-full max-w-100 items-center gap-3 rounded-[12px] py-3 pl-4 pr-3 shadow-e2',
        error
          ? 'border border-danger-border bg-surface text-ink'
          : 'bg-ink-panel text-ink-panel-text',
      )}
    >
      <span
        className={cn(
          'grid size-5.5 shrink-0 place-items-center rounded-full',
          tone === 'success' && 'bg-accent-mint text-ink',
          tone === 'ai' && 'bg-primary-tint text-primary',
          error && 'bg-danger text-white',
        )}
      >
        {error ? (
          <span aria-hidden="true" className="text-[13px] font-bold">
            !
          </span>
        ) : (
          <Icon icon={tone === 'ai' ? Sparkles : Check} size={16} />
        )}
      </span>

      <span className="flex-1 text-[15px]">{toast.message}</span>

      {toast.action ? (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick();
            onDismiss(toast.id);
          }}
          className={cn(
            'focus-ring shrink-0 rounded-chip px-1.5 text-small font-bold',
            error ? 'text-danger' : 'text-accent-mint',
          )}
        >
          {toast.action.label}
        </button>
      ) : (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            onDismiss(toast.id);
          }}
          className={cn(
            'focus-ring grid size-8 shrink-0 place-items-center rounded-chip',
            error ? 'text-muted' : 'text-ink-panel-dim',
          )}
        >
          <Icon icon={X} size={16} />
        </button>
      )}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast requires ToastProvider');
  return ctx;
}
